import { useEffect } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import i18n from "./i18n/i18n";
import { initHumanBeacon } from "./api/beacon";
import { AuthProvider } from "./auth/AuthContext";
import { TeamProvider } from "./context/TeamContext";
import { ToastProvider } from "./context/ToastContext";
import { AppLayout } from "./components/layout/AppLayout";
import PrivateRoute from "./components/PrivateRoute";
import AdminRoute from "./components/AdminRoute";
import StartPage from "./components/StartPage";
import LoginForm from "./components/LoginForm";
import SignupForm from "./components/SignupForm";
import DashboardPage from "./components/DashboardPage";
import TeamFormPage from "./components/TeamFormPage";
import ProfilePage from "./pages/profilepage/ProfilePage";
import MatchPage from "./pages/matchpage/MatchPage";
import AdminPage from "./pages/adminpage/AdminPage";
import CupPage from "./pages/cuppage/CupPage";
import CupsHubPage from "./pages/cupspage/CupsHubPage";
import HelpPage from "./pages/helppage/HelpPage";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import DevEngine2Page from "./pages/DevEngine2Page"; // DEV-ONLY engine2 viewer

function App() {
  // First paint is always English (matches the prerendered snapshot). Once
  // mounted — i.e. after hydration — restore an anonymous visitor's previously
  // chosen language. Logged-in users get their account locale via AuthContext.
  useEffect(() => {
    const saved = localStorage.getItem("tactics_lang");
    if (saved && saved !== i18n.language) i18n.changeLanguage(saved);
    // Count real humans: fires once per session after the first interaction.
    initHumanBeacon();
  }, []);

  return (
    <AuthProvider>
      <TeamProvider>
        <ToastProvider>
        <Router>
          <Routes>
            {/* Auth pages — no nav, no ads */}
            <Route path="/" element={<StartPage />} />
            <Route path="/login" element={<LoginForm />} />
            <Route path="/signup" element={<SignupForm />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />

            {/* App pages — shared layout with nav + ad banner */}
            <Route
              path="/dashboard"
              element={
                <PrivateRoute>
                  <AppLayout><DashboardPage /></AppLayout>
                </PrivateRoute>
              }
            />
            <Route
              path="/team/create"
              element={
                <PrivateRoute>
                  <AppLayout><TeamFormPage /></AppLayout>
                </PrivateRoute>
              }
            />
            <Route
              path="/team/edit"
              element={
                <PrivateRoute>
                  <AppLayout><TeamFormPage /></AppLayout>
                </PrivateRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <PrivateRoute>
                  <AppLayout><ProfilePage /></AppLayout>
                </PrivateRoute>
              }
            />

            <Route
              path="/match/:matchId"
              element={
                <PrivateRoute>
                  <AppLayout><MatchPage /></AppLayout>
                </PrivateRoute>
              }
            />

            <Route
              path="/cups"
              element={
                <PrivateRoute>
                  <AppLayout><CupsHubPage /></AppLayout>
                </PrivateRoute>
              }
            />
            <Route
              path="/cup/:cupId"
              element={
                <PrivateRoute>
                  <AppLayout><CupPage /></AppLayout>
                </PrivateRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <PrivateRoute>
                  <AdminRoute>
                    <AppLayout><AdminPage /></AppLayout>
                  </AdminRoute>
                </PrivateRoute>
              }
            />

            <Route path="/help" element={<AppLayout><HelpPage /></AppLayout>} />

            {/* DEV-ONLY: engine2 game in the viewer (no backend). Remove when done. */}
            <Route path="/dev/engine2" element={<DevEngine2Page />} />

            <Route path="*" element={<h2>404: Page Not Found</h2>} />
          </Routes>
        </Router>
        </ToastProvider>
      </TeamProvider>
    </AuthProvider>
  );
}

export default App;
